import { Expose, Transform } from 'class-transformer';
import { Point } from 'geojson';

import { SnModel } from '@lib/common/models';

import { IFsLprResult } from '../interfaces/common/result';
import { FsLprAxonVIAgencyInfo } from './lpr-axon-vi-agency-info';
import { plateCoords, HotlistInfo } from './lpr-fleet-read';

// This model class is used to represent the data returned from the Axon VI LPR API.
export class FsLprAxonVIRead extends SnModel implements IFsLprResult {
  @Expose()
  public id: string;

  // name of the camera
  @Expose()
  public name: string;

  // camera model from the API
  @Expose()
  public cameraModel?: string;

  // owner of the camera
  @Expose()
  public owner: FsLprAxonVIReadOwner;

  @Expose()
  public plate: string;

  @Expose()
  public plateImageUrl: string;

  @Expose()
  public contextImageUrl: string;

  @Expose()
  @Transform((value) => {
    // This should be handled on the backend; Remove this @Transform if/when that's done.
    if (value?.color?.name?.toLowerCase() === 'unknown') {
      return {
        ...value,
        color: {
          ...value.color,
          name: '',
        },
      };
    }

    return value;
  })
  public vehicleProperties: FsLprAxonVIReadVehicleProperties;

  /**
   * sortValues is passed a query parameter to the API in order to start the search from a specific value
   * Take the last value in the previous result set and use that to start the next search (essentially pagination)
   */
  @Expose()
  public sortValues: [number, string];

  @Expose()
  public type: string;

  // deprecated
  @Expose()
  public confidence: string;

  @Expose()
  public organization: string;

  @Expose()
  public isHit: boolean;

  @Expose()
  public hitReason: string;

  @Expose()
  public geometry: Point;

  @Expose()
  @Transform((value) => {
    // This should be handled on the backend; Remove this @Transform if/when that's done.
    if (value?.toLowerCase() === 'unknown') {
      return '';
    }
    return value;
  })
  public state: string;

  @Expose()
  public stateConfidence: number;

  @Expose()
  public subdivisionCode: string;

  @Expose()
  public subdivisionConfidence: number;

  @Expose()
  public createdTimestamp: Date;

  @Expose()
  public overviewImageKey: string;

  @Expose()
  public plateImageKey: string;

  @Expose()
  public integratedLprType?: string;

  @Expose()
  public imageCoordinates: plateCoords;

  @Expose()
  public plateBoundingBox: plateCoords;

  @Expose()
  public camera: FsLprAxonCameraProperties;

  @Expose()
  public hotlistInfo: HotlistInfo;

  @Expose()
  public isSharedRecord: boolean;

  @Expose()
  public agencyInfo: FsLprAxonVIAgencyInfo;

  // cropped context image url that includes the plate
  public croppedContextImageUrl: string;

  get cameraName(): string {
    return this.name || this.cameraModel || '';
  }

  get vehicleColor(): string {
    return this.vehicleProperties?.color?.name || '';
  }

  get vehicleColorConfidence(): number {
    return this.vehicleProperties?.color?.confidence || 0;
  }

  get vehicleMake(): string {
    return this.vehicleProperties?.make?.name || '';
  }

  get vehicleMakeConfidence(): number {
    return this.vehicleProperties?.make?.confidence || 0;
  }

  get vehicleBody(): string {
    return this.vehicleProperties?.body?.name || '';
  }

  get vehicleBodyConfidence(): number {
    return this.vehicleProperties?.body?.confidence || 0;
  }

  get displayState(): string {
    return this.subdivisionCode || this.state;
  }

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }
}

export interface FsLprAxonVIReadVehicleProperties {
  color: {
    name: string;
    confidence: number;
  };
  make: {
    name: string;
    confidence: number;
  };
  body: {
    name: string;
    confidence: number;
  };
}

export interface FsLprAxonVIReadOwner {
  firstName: string;
  lastName: string;
  id: string;
  badgeNumber: string;
}

export interface FsLprAxonCameraProperties {
  id: string;
  name: string;
  model: string;
  vendor: string;
}
